import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { useGoogleLogin } from "@react-oauth/google";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import Layout from "../components/Layout";
import ApiCall from "../services/apiCall";
import Hero from "../components/Hero";

const SignIn = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const [loading, setLoading] = useState();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isPhoneNumber, setIsPhoneNumber] = useState({
    active: true,
    value: "",
  });

  const onSignInWithEmail = async () => {
    const minPasswordLength = 6;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (password.length < minPasswordLength) {
      toast.error(
        `Password must be at least ${minPasswordLength} characters long.`
      );
      return;
    }

    const data = JSON.stringify({
      username: email,
      password: password,
    });

    setLoading(true);
    try {
      const res = await ApiCall("api/login", "POST", data);
      if (res.ok) {
        localStorage.setItem("userData", JSON.stringify(res.data));
        localStorage.setItem("userToken", JSON.stringify(res.meta.token));
        toast.success(res.message || "Logged in successfully!");
        navigate("/");
      } else {
        toast.error(
          res.errors?.length
            ? res.errors[0].msg
            : res.message || "Something went wrong"
        );
      }
    } catch (error) {
      toast.error(error.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const onSignInWithPhone = async () => {
    const data = JSON.stringify({
      phoneNumber: isPhoneNumber.value,
    });

    setLoading(true);
    try {
      const res = await ApiCall("api/send-otp", "POST", data);
      if (res.ok) {
        navigate("/otp", {
          state: {
            value: isPhoneNumber.value,
            isPhoneNumber: true,
          },
        });
        toast.success(res.message || "OTP sent successfully.");
      } else {
        toast.error(res.message || "Something went wrong");
      }
    } catch (error) {
      toast.error(error.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const onSingInWithGmail = useGoogleLogin({
    onSuccess: (response) => onSuccess(response),
    onError: (error) => onError(error),
    scope: "profile email",
  });

  const onError = (res) => {
    console.log("Login failed: res:", res);
    alert(`Failed to login. Please ping this to support: ${res.details}`);
  };

  const fetchGoogleUserProfile = async (accessToken) => {
    try {
      const res = await fetch("https://www.googleapis.com/oauth2/v2/userinfo", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const userInfo = await res.json();
      return userInfo;
    } catch (error) {
      console.error("Error fetching Google user profile:", error);
    }
  };

  const onSuccess = (response) => {
    const accessToken = response.access_token;
    try {
      fetchGoogleUserProfile(accessToken).then((userInfo) => {
        const data = {
          name: userInfo.name,
          firstName: userInfo.given_name,
          lastName: userInfo.family_name,
          email: userInfo.email,
          username: userInfo.email,
          profilePhotoUrl: userInfo.picture,
          accessToken: accessToken,
          accessTokenExpiresAt: "",
        };
        ApiCall("api/oauth/google", "POST", JSON.stringify(data))
          .then((res) => {
            if (res.ok) {
              localStorage.setItem("userData", JSON.stringify(res.data));
              localStorage.setItem("userToken", JSON.stringify(res.meta.token));
              navigate("/");
            }
          })
          .catch((error) => {
            toast.error(error.message);
          });
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const responseFacebook = async (response) => {
    const { accessToken, userID, name, email } = response || {};
    if (accessToken && userID) {
      const data = {
        name,
        email,
        username: email,
        profilePhotoUrl: response.picture.data.url,
        accessToken,
        accessTokenExpiresAt: "",
      };
      try {
        const res = await ApiCall(
          "api/oauth/facebook",
          "POST",
          JSON.stringify(data)
        );
        if (res.ok) {
          localStorage.setItem("userData", JSON.stringify(res.data));
          localStorage.setItem("userToken", JSON.stringify(res.meta.token));
          navigate("/");
          toast.success("Logged in successfully!");
        } else {
          toast.error(res.message || "Something went wrong");
        }
      } catch (error) {
        toast.error(error.message || "An unexpected error occurred");
      }
    } else {
      toast.error("Failed to login with Facebook");
    }
  };

  return (
    <Layout>
      <Hero title="Sign In" />
      <section
        className="login-section section-gap wow fadeInUp"
        data-wow-delay="0.3s"
      >
        <div className="midd-section">
          <div className="row">
            <div className="col-lg-12">
              <div className="title-sec">
                <h2 className="title-30 wow fadeInUp" data-wow-delay="0.2s">
                  Sign In
                </h2>
              </div>
              <div className="form-sec">
                <form
                  className="wow fadeInUp"
                  data-wow-delay="0.2s"
                  onSubmit={(e) => {
                    e.preventDefault();
                    isPhoneNumber.active
                      ? onSignInWithPhone()
                      : onSignInWithEmail();
                  }}
                >
                  <div className="input-sec">
                    <input
                      type={isPhoneNumber.active ? "tel" : "email"}
                      name={isPhoneNumber.active ? "phone" : "email"}
                      placeholder={
                        isPhoneNumber.active
                          ? "Enter Your Mobile Number"
                          : "Enter Your Email"
                      }
                      className="form-control"
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        if (isPhoneNumber.active) {
                          setIsPhoneNumber({
                            ...isPhoneNumber,
                            value: inputValue,
                          });
                        } else {
                          setEmail(inputValue);
                        }
                      }}
                    />
                  </div>
                  {!isPhoneNumber.active ? (
                    <div className="input-sec mt-4">
                      <input
                        type={showPassword ? "text" : "password"}
                        name={"password"}
                        placeholder={"Enter Your Password"}
                        className="form-control"
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          setPassword(inputValue);
                        }}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        <i className={showPassword ? "fas fa-eye" : "fas fa-eye-slash"}></i>
                      </button>
                    </div>
                  ) : null}
                  <div className="btn-sec">
                    <button
                      type="button"
                      className="btn"
                      onClick={
                        isPhoneNumber.active
                          ? onSignInWithPhone
                          : onSignInWithEmail
                      }
                    >
                      Sign In
                    </button>
                  </div>
                </form>
                <div className="sign-option">
                  {!isPhoneNumber.active ? (
                    <Link
                      to="/forgot-password"
                      className="p-18 wow fadeInUp"
                      data-wow-delay="0.2s"
                    >
                      Forgot password?
                    </Link>
                  ) : null}
                  <span className="wow fadeInUp" data-wow-delay="0.2s">
                    <a
                      href="#"
                      className="title-20"
                      onClick={() => {
                        setIsPhoneNumber({
                          ...isPhoneNumber,
                          active: !isPhoneNumber.active,
                        });
                      }}
                    >
                      Sign in with{" "}
                      {isPhoneNumber.active ? "Email" : "Mobile Number"}
                    </a>
                  </span>
                  <p className="p-18 wow fadeInUp" data-wow-delay="0.2s">
                    Or Sign in with
                  </p>
                  <div className="sign-social-option">
                    <a
                      href="#"
                      className="wow fadeInUp"
                      data-wow-delay="0.2s"
                      onClick={onSingInWithGmail}
                    >
                      <i className="fab fa-google"></i>
                    </a>
                    <FacebookLogin
                      appId="152339711194724"
                      autoLoad={false}
                      fields="name,email,picture"
                      scope="public_profile,email"
                      callback={responseFacebook}
                      render={(renderProps) => (
                        <a
                          href="#"
                          className="wow fadeInUp"
                          data-wow-delay="0.2s"
                          onClick={renderProps.onClick}
                          style={{
                            display: "inline-block",
                            // color: "#3b5998",
                            textDecoration: "none",
                            fontSize: "16px",
                          }}
                        >
                          <i className="fab fa-facebook-f"></i>
                        </a>
                      )}
                    />
                    {/* <a href="#" className="wow fadeInUp" data-wow-delay="0.2s">
                      <i className="fab fa-apple"></i>
                    </a> */}
                  </div>
                  <span
                    className="sign-up-option wow fadeInUp"
                    data-wow-delay="0.2s"
                  >
                    <p>
                      Don't have an account? <Link to={"/signup"}>Sign Up</Link>
                    </p>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <a href="#" className="back-to-top">
        <i className="fa fa-chevron-up"></i>
      </a>
      <ToastContainer />
    </Layout>
  );
};

export default SignIn;
